import { Component, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { ButtonSizeEnum } from '../../../shared/components/button/types/button-size.enum';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { SpinnerColorEnum } from '../../../shared/components/spinner/types/spinner-color.enum';
import { SpinnerSizeEnum } from '../../../shared/components/spinner/types/spinner-size.enum';
import { Category } from '../../../shared/models/interfaces/category.interface';
import { CategoryService } from '../../../shared/services/category/category.service';
import { ColorPickerDirective } from 'ngx-color-picker';
import { CreateCategoryForm } from '../../../shared/models/interfaces/forms/create-category-form.type';
import { CreateCategoryPayload } from '../../../shared/types/payloads/create-category.payload';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../../shared/utils/functions';

@Component({
  selector: 'l-create-category-modal',
  standalone: true,
  templateUrl: './create-category-modal.component.html',
  imports: [
    ReactiveFormsModule,
    NgClass,
    IconComponent,
    ButtonComponent,
    InputComponent,
    SpinnerComponent,
    ColorPickerDirective,
  ],
})
export class CreateCategoryModalComponent implements OnInit {

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig,
    private readonly categoryService: CategoryService,
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
  ) {
    this.formGroup = this.formBuilder.group<CreateCategoryForm>({
      name: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      shortDescription: new FormControl<string>('', { nonNullable: true }),
      description: new FormControl<string>('', { nonNullable: true }),
      color: new FormControl<string>('#F54297', { nonNullable: true, validators: [Validators.required] }),
    });
  }

  public isSubmitting = false;

  public readonly descriptionMax = 250;

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public readonly spinnerColorEnum = SpinnerColorEnum;

  public readonly spinnerSizeEnum = SpinnerSizeEnum;

  public formGroup!: FormGroup<CreateCategoryForm>;

  public category?: Category;
  
  public isUpdate: boolean = false;

  public get modalTitle(): string {
    return this.isUpdate ? 'Editar categoria' : 'Criar nova categoria';
  }

  public get submitLabel(): string {
    return this.isUpdate ? 'Salvar' : 'Criar';
  }

  public get descriptionCount(): number {
    return this.formGroup.controls.description.value?.length ?? 0;
  }

  public get isDisabled(): boolean {
    return this.formGroup.invalid || this.isSubmitting;
  }

  public get nameControl(): FormControl<string> {
    return this.formGroup.controls.name;
  }

  public get colorControl(): FormControl<string> {
    return this.formGroup.controls.color;
  }

  public async ngOnInit(): Promise<void> {
    if (this.config.data?.category) {
      this.category = this.config.data.category;
      this.isUpdate = this.config.data.isUpdate ?? false;

      if (this.isUpdate) {
        const category = this.category!;

        this.formGroup.patchValue({
          name: category.name ?? '',
          shortDescription: category.shortDescription ?? '',
          description: category.description ?? '',
          color: category.color ?? '#F54297',
        });
      }
    }
  }

  public close(): void {
    this.ref.close(false);
  }

  public onColorPickerChange(color: unknown): void {
    if (typeof color !== 'string') {
      return;
    }

    this.formGroup.controls.color.setValue(color);
    this.formGroup.controls.color.markAsTouched();
  }

  public async submit(): Promise<void> {
    this.formGroup.markAllAsTouched();

    try {
      if (this.isDisabled) {
        return;
      }

      this.isSubmitting = true;

      const formValue = this.formGroup.getRawValue();
      const payload: CreateCategoryPayload = {
        ...formValue,
        shortDescription: formValue.shortDescription || ''
      };

      if (this.isUpdate) {
        const categoryId = this.category?.id;

        if (!categoryId) {
          throw new Error('Categoria não encontrada para edição.');
        }

        await this.categoryService.update(categoryId, payload);
      } else {
        await this.categoryService.create(payload);
      }

      this.ref.close(true);
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isSubmitting = false;
    }
  }
}
